import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectCommunityLinks } from "src/store/common"
import { addMessage } from "src/store/messages"
import useLocalizedPath from "src/hooks/useLocalizedPath"
import { COPIED, FAILED, canShare, shareLink } from "src/utils/share"

import Modal from "src/components/modals/Modal"
import { ShareIcon } from "src/components/icons"
import "./TeamModal.css"

/**
 * Who makes the game — one developer — and how a player can help: credits, friends, the networks.
 *
 * Opened from the heart beside the balance in the header, signed in or not. The address and the
 * links come from `GET /game-config/` (`community`), so one the server does not name is absent.
 *
 * The share button is the "play with your friends" point made pressable: it hands the site's
 * address to the device's share sheet (WhatsApp, Instagram messages, whatever is installed —
 * `utils/share.js` has why the sheet and not a row of network buttons), or to the clipboard
 * where there is none, and the button says which it will do. The address is the page's own
 * origin at the reader's language — a Spanish player sends `/es`, the page they were shown —
 * so a deployed bundle needs no domain written into it.
 */
const TeamModal = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const localized = useLocalizedPath()
  const links = useSelector(selectCommunityLinks)
  const sheet = canShare()

  const share = async () => {
    const url = `${window.location.origin}${localized("/")}`
    const outcome = await shareLink({ url, title: "Zombies vs Humans", text: t("team.shareText") })
    if (outcome === COPIED) dispatch(addMessage({ text: t("team.shareCopied"), type: "success" }))
    if (outcome === FAILED) dispatch(addMessage({ text: t("team.shareFallback", { url }), type: "info" }))
  }

  return (
    <Modal width="560px">
      <div className="team-panel">
        <h1>{t("team.title")}</h1>

        <p className="team-lead">{t("team.lead")}</p>

        <p className="section-label team-label">{t("team.helpTitle")}</p>
        <ul className="team-points">
          {t("team.points", { returnObjects: true }).map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>

        <p className="section-label team-label">{t("team.shareTitle")}</p>
        <p className="team-share-lead">{t("team.shareLead")}</p>
        <button type="button" className="team-share" onClick={share}>
          <ShareIcon size={18} />
          {sheet ? t("team.share") : t("team.copyLink")}
        </button>

        {links.length > 0 && (
          <>
            <p className="section-label team-label">{t("team.contact")}</p>
            <div className="team-links">
              {links.map(({ network, url }) => (
                <a
                  key={network}
                  href={url}
                  // A mailto hands off to the mail app; in a new tab it leaves a blank one behind.
                  {...(network === "email" ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                  className={`team-link ${network}`}
                >
                  {t(`team.networks.${network}`)}
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default TeamModal
